import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { isScheduledOrActivePlanPause } from 'src/functions/planPause.functions';
import { dateToLocaleDateString } from 'src/hooks/Intercom/Intercom.functions';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { tLeadsByFunnel } from 'src/pages/Dashboard/Dashboard.types';
import { AnalyticsService } from '../../../../../../../pages/Analytics/Analytics.services';
import { useSubscriptionContext } from '../../../../../../../providers/SubscriptionProvider/Subscription.context';
import {
  ePlanCode,
  ePlanFrequency,
} from '../../../../../../../providers/SubscriptionProvider/Subscription.enums';
import { useWorkspaceContext } from '../../../../../../../providers/WorkspaceProvider/Workspace.context';
import { CANCELLATION_REASON_CODES } from '../../../SubscriptionManager.constants';
import { useSubscriptionManagerContext } from '../../../SubscriptionManager.context';
import { ePlanCurrency } from '../../../SubscriptionManager.enums';
import {
  adaptRetentionOffer,
  readBillingRefusalCode,
  SubscriptionManagerService,
} from '../../../SubscriptionManager.service';
import {
  tCancellationReason,
  tInvoicePreview,
  tRetentionFlowError,
  tRetentionFlowResponse,
  tRetentionHypothesisCode,
  tRetentionOffer,
  tRetentionOfferAnswer,
} from '../../../SubscriptionManager.types';
import {
  resolveRetentionSessionCta,
  retentionBookingUrl,
} from './CancelSubscription.functions';
import {
  tCancelSubscriptionContextValue,
  tSelectReasonOptions,
} from './CancelSubscription.types';

/**
 * Why a cancellation was scheduled, as reported on the s4b event:
 * - `offer_rejected`: the customer declined the offer shown.
 * - `no_offer`: the server had no offer for the reason.
 * - `offer_lookup_failed`: the offer could not be read and the customer chose
 *   to cancel anyway.
 * - `offer_ineligible`: the offer stopped applying on accept and the customer
 *   chose to cancel.
 */
type tCancellationOutcome =
  | 'offer_rejected'
  | 'no_offer'
  | 'offer_lookup_failed'
  | 'offer_ineligible';

type tScheduleCancellationOptions = {
  reason: tCancellationReason | null;
  outcome: tCancellationOutcome;
  ineligibleReason: string | null;
};

type tOfferAnalyticsOptions = {
  offer: tRetentionOffer;
  reason: tCancellationReason | null;
};

function hypothesisFor(
  reason: tCancellationReason | null,
): tRetentionHypothesisCode {
  return reason ? CANCELLATION_REASON_CODES[reason].hypothesis : 'other';
}

/**
 * The `offer_type` values the funnel already segments on. An extension is
 * framed as a results program for "no veo resultados" and as a technical
 * session otherwise, so the label follows the modal the customer saw.
 */
function offerAnalyticsType(options: tOfferAnalyticsOptions): string {
  const { offer, reason } = options;
  switch (offer.type) {
    case 'DISCOUNT':
      return 'discount';
    case 'DOWNGRADE':
      return 'downgrade';
    case 'PLAN_PAUSE':
      return 'plan_pause';
    case 'EXTENSION':
      return reason === 'not_seeing_results'
        ? 'onboarding_session'
        : 'eng_session';
  }
}

/** The offer's terms as the server resolved them, for the s3 events. */
function offerAnalyticsDetails(
  options: tOfferAnalyticsOptions,
): Record<string, string | number> {
  const { offer } = options;
  const details = {
    offer_type: offerAnalyticsType(options),
    offer_code: offer.code,
  };

  switch (offer.type) {
    case 'DISCOUNT':
      return {
        ...details,
        discount_percentage: offer.percentOff,
        discount_duration_months: offer.durationMonths,
      };
    case 'EXTENSION':
      return { ...details, free_days: offer.durationDays };
    case 'DOWNGRADE':
      return { ...details, new_plan: 'basic' };
    case 'PLAN_PAUSE':
      return details;
  }
}

/**
 * An accept is a success only when the call answered 2xx with a success body
 * AND the offer itself reports it applied. Anything else (a refusal, a network
 * failure, an offer that did not apply) is not shown as a success.
 */
function isAppliedOfferResponse(
  response: tRetentionFlowResponse | undefined,
): boolean {
  return (
    response?.success === true && response.data?.offer_result?.success === true
  );
}

export default function useCancelSubscription(): tCancelSubscriptionContextValue {
  const {
    showCancelSubscriptionModal,
    cancelSubscriptionFormik,
    generalCancellationProperties,
    resetCancellationModalStates,
  } = useSubscriptionManagerContext();
  const { workspaceInfo, currentWorkspace } = useWorkspaceContext();
  const { subscription, getPlans, getSummary, plans } =
    useSubscriptionContext();
  const { trackEvent } = useAnalytics();

  const [showForm, setShowForm] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [cancellationReason, setCancellationReason] =
    useState<tCancellationReason | null>(null);
  const [retentionOffer, setRetentionOffer] = useState<tRetentionOffer | null>(
    null,
  );
  const [appliedRetentionOffer, setAppliedRetentionOffer] =
    useState<tRetentionOffer | null>(null);
  const [appliedPlanPauseMonths, setAppliedPlanPauseMonths] = useState<
    1 | 2 | 3 | null
  >(null);
  const [retentionFlowError, setRetentionFlowError] =
    useState<tRetentionFlowError | null>(null);
  const [leadsByFunnel, setLeadsByFunnel] = useState<tLeadsByFunnel[]>([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [sentMessagesCount, setSentMessagesCount] = useState<number>(0);
  const [retentionFlowSessionId, setRetentionFlowSessionId] = useState<
    number | null
  >(null);
  const [offerLastAcceptedAt, setOfferLastAcceptedAt] = useState<string | null>(
    null,
  );
  const [isLoadingReasonSelection, setIsLoadingReasonSelection] =
    useState(false);
  const [isLoadingOfferAcceptance, setIsLoadingOfferAcceptance] =
    useState(false);
  const [isLoadingFinalization, setIsLoadingFinalization] = useState(false);
  const [planNotSuitableInvoicePreview, setPlanNotSuitableInvoicePreview] =
    useState<tInvoicePreview | null>(null);
  // True once this attempt's retention session got its final action confirmed
  // (the customer's decline, or the cancel recorded before scheduling), so the
  // outcome is never written twice. Scoped to the attempt: reset with the flow.
  const finalActionRecordedRef = useRef(false);

  const users = Object.values(workspaceInfo?.users ?? {});

  /**
   * Where the success screen's session CTA goes: a 1:1 on the booking page for
   * the reason, for every plan at any hour; not offered at all when this
   * environment configures no booking page.
   */
  const retentionSessionCta = resolveRetentionSessionCta(
    retentionBookingUrl(cancellationReason),
  );

  useEffect(() => {
    async function fetchData() {
      if (showCancelSubscriptionModal && currentWorkspace?.id) {
        setIsLoadingLeads(true);

        const defaultEndDate = moment(new Date()).subtract(1, 'day').toDate();
        const defaultStartDate = moment(defaultEndDate)
          .subtract(1, 'month')
          .toDate();

        const query = {
          wid: currentWorkspace.id,
          start_date: moment(defaultStartDate)
            .utc()
            .startOf('day')
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          end_date: moment(defaultEndDate)
            .utc()
            .endOf('day')
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        };

        try {
          const [leadsData, analyticsResponse, retentionFlowResponse] =
            await Promise.all([
              SubscriptionManagerService.getLeadsByFunnel(currentWorkspace.id),
              AnalyticsService.getAnalyticsData(query),
              SubscriptionManagerService.startRetentionFlow(
                currentWorkspace.id,
              ),
            ]);

          setLeadsByFunnel(leadsData);

          const messageCountResponse = analyticsResponse?.data?.MessageCount;
          const sentMessages =
            messageCountResponse?.current_period?.sent_messages || 0;
          setSentMessagesCount(sentMessages);

          if (
            retentionFlowResponse?.success &&
            retentionFlowResponse?.data?.id
          ) {
            setRetentionFlowSessionId(retentionFlowResponse.data.id);
            // Store offer_last_accepted_at for rechurn tracking when reason is selected
            if (retentionFlowResponse.data.offer_last_accepted_at) {
              setOfferLastAcceptedAt(
                retentionFlowResponse.data.offer_last_accepted_at,
              );
            }
          }

          trackEvent('front.settings.retention_flow_started', {
            ...generalCancellationProperties,
            user_leads_count: leadsData.reduce(
              (sum, funnel) => sum + funnel.totalLeads,
              0,
            ),
            funnel_count: leadsData.filter(
              (funnel) => funnel.name !== 'Inbox 📥',
            ).length,
            user_conversations_count: 0,
            users_count: users.length,
            sent_messages: sentMessages,
          });
        } catch (error) {
          console.error('Error fetching data:', error);
          setLeadsByFunnel([]);
          setSentMessagesCount(0);
        } finally {
          setIsLoadingLeads(false);
        }
      } else {
        setLeadsByFunnel([]);
        setSentMessagesCount(0);
        setIsLoadingLeads(false);
      }
    }

    fetchData();
  }, [showCancelSubscriptionModal, currentWorkspace?.id]);

  // RET-1244: getPlans() resolves currency server-side now (BFF), so the former
  // subscription.currency gate — which existed only to avoid the old client-side
  // geolocation fallback — is no longer needed.
  useEffect(() => {
    if (showCancelSubscriptionModal && currentWorkspace?.id && !plans) {
      getPlans();
    }
  }, [showCancelSubscriptionModal, currentWorkspace?.id, plans, getPlans]);

  // Reset local cancellation states
  function resetLocalCancellationStates() {
    setShowForm(false);
    setShowOfferModal(false);
    setShowConfirmationModal(false);
    setShowSchedulingModal(false);
    setCancellationReason(null);
    setRetentionOffer(null);
    setAppliedRetentionOffer(null);
    setAppliedPlanPauseMonths(null);
    setRetentionFlowError(null);
    setRetentionFlowSessionId(null);
    setOfferLastAcceptedAt(null);
    setIsLoadingReasonSelection(false);
    setIsLoadingOfferAcceptance(false);
    setIsLoadingFinalization(false);
    setPlanNotSuitableInvoicePreview(null);
    finalActionRecordedRef.current = false;
  }

  const activeFunnelsCount = leadsByFunnel.filter(
    (funnel) => funnel.name !== 'Inbox 📥',
  ).length;
  const totalLeadsCount = leadsByFunnel.reduce(
    (sum, funnel) => sum + funnel.totalLeads,
    0,
  );

  const hasZeroLeads = !isLoadingLeads && totalLeadsCount === 0;

  // Get first 3 user names for display
  const userNames = users.slice(0, 3).map((user) => user.name.split(' ')[0]);
  const remainingUsersCount = Math.max(0, users.length - 3);

  const handleKeepSubscription = () => {
    if (showForm) {
      // If in form step, close the modal
      resetLocalCancellationStates();
      resetCancellationModalStates();
    } else {
      // If in retention step, track event and close modal
      trackEvent('front.settings.retention_flow_s1_cta_clicked', {
        ...generalCancellationProperties,
        cta_type: 'keep_subscription',
      });
      resetLocalCancellationStates();
      resetCancellationModalStates();
    }
  };

  /**
   * Step back from the offer to the reasons. The offer screen is reached by choosing a
   * reason, so "back" means re-opening that choice — not closing the flow, which would
   * make a customer who wants to pick a different reason start over and would drop the
   * retention session they are already in.
   */
  const handleBackToReasons = () => {
    // Continuing overwrote the form's `cancellationReason` with the label the customer
    // read, because schedule-cancelation records it that way. The radios match on the
    // CODE, so without putting it back nothing is selected on the way in — and since the
    // textarea only renders for the selected reason, the description they already typed
    // would look lost too. It is still in the form; restoring the code reveals it.
    if (cancelSubscriptionFormik && cancellationReason) {
      cancelSubscriptionFormik.setFieldValue(
        'cancellationReason',
        cancellationReason,
      );
    }

    setShowOfferModal(false);
    setShowForm(true);
  };

  const handleContinue = () => {
    trackEvent('front.settings.retention_flow_s1_cta_clicked', {
      ...generalCancellationProperties,
      cta_type: 'continue_cancellation',
    });
    setShowForm(true);
    trackEvent('front.settings.retention_flow_s2_viewed', {
      ...generalCancellationProperties,
    });
  };

  const handleClose = () => {
    if (!showForm && !showOfferModal && !showConfirmationModal) {
      // Only track if closing from retention step (S1)
      trackEvent('front.settings.retention_flow_s1_cta_clicked', {
        ...generalCancellationProperties,
        cta_type: 'close',
      });
    }

    // Abort retention flow if session exists and user is closing without completing
    if (
      currentWorkspace?.id &&
      retentionFlowSessionId &&
      !showConfirmationModal
    ) {
      (async () => {
        try {
          await SubscriptionManagerService.abortRetentionFlow(
            currentWorkspace.id,
          );
        } catch (error) {
          console.error('Error aborting retention flow:', error);
        }
      })();
    }

    // An accept that did not confirm may still have applied (a lost response):
    // refresh so the subscription screen shows what the server actually has.
    if (retentionFlowError?.stage === 'accept') {
      void getSummary();
    }

    resetLocalCancellationStates();
    resetCancellationModalStates();
  };

  const trackRechurn = (hypothesis: tRetentionHypothesisCode) => {
    if (!offerLastAcceptedAt) return;

    trackEvent('front.settings.retention_flow_user_rechurned', {
      ...generalCancellationProperties,
      original_retention_date: moment(offerLastAcceptedAt).format('YYYY-MM-DD'),
      days_since_retention: moment().diff(moment(offerLastAcceptedAt), 'days'),
      // The session answer does not say which hypothesis the earlier offer
      // answered.
      original_hypothesis: 'unknown',
      new_hypothesis: hypothesis,
    });
  };

  /**
   * Records on the retention session that the customer is cancelling — the
   * final action a decline sends, which the server accepts on any open session,
   * whether or not an offer was shown. True only when the server confirmed it.
   * Best effort by design: a recording that fails or is refused never keeps the
   * customer from the cancellation they asked for.
   */
  const recordCancelDecision = async (): Promise<boolean> => {
    if (!currentWorkspace?.id) return false;

    try {
      const response = await SubscriptionManagerService.finalizeRetentionAction(
        currentWorkspace.id,
        {
          phase: 'final_action',
          offer_accepted: false,
          final_action: 'cancel',
        },
      );
      const recorded = response?.success === true;
      if (recorded) finalActionRecordedRef.current = true;
      return recorded;
    } catch (error) {
      console.error(
        'Error recording the cancellation on the retention session:',
        error,
      );
      return false;
    }
  };

  /**
   * A cancellation that does not follow a decline (no offer, or the customer
   * continuing from an error) still closes its session with that outcome before
   * it is scheduled. A session a confirmed decline already closed is not
   * written again.
   */
  const recordCancelDecisionUnlessRecorded = async () => {
    if (finalActionRecordedRef.current) return;
    await recordCancelDecision();
  };

  /**
   * Schedules the cancellation through the manager's cancellation form, whose
   * submit handler calls schedule-cancelation, shows the success or error toast
   * and closes the modal. It resolves true only when leadbilling confirmed, so
   * the scheduled event is emitted from the branch that saw the confirmation.
   */
  const scheduleCancellation = async (
    options: tScheduleCancellationOptions,
  ) => {
    const { reason, outcome, ineligibleReason } = options;
    let scheduled = false;

    try {
      if (cancelSubscriptionFormik) {
        // Formik resolves submitForm with what onSubmit returned (FormikProps
        // types it Promise<any>; the helper overload says void). The manager's
        // handler returns true only for a confirmed schedule.
        const submitCancellation: () => Promise<unknown> =
          cancelSubscriptionFormik.submitForm;
        scheduled = (await submitCancellation()) === true;
      }
    } catch (error) {
      console.error('Error scheduling the cancellation:', error);
    }

    if (!scheduled) return;

    const endDate = subscription?.endDate ? moment(subscription.endDate) : null;
    trackEvent('front.settings.retention_flow_s4b_cancellation_scheduled', {
      ...generalCancellationProperties,
      reason: outcome,
      original_hypothesis: hypothesisFor(reason),
      ...(ineligibleReason
        ? { offer_ineligible_reason: ineligibleReason }
        : {}),
      // The subscription ends at the close of its paid period; with no such date
      // the property is omitted rather than guessed.
      ...(endDate?.isValid()
        ? { cancellation_date: endDate.format('YYYY-MM-DD') }
        : {}),
    });
  };

  const handleSelectReason = async (options: tSelectReasonOptions) => {
    const { reason, reasonLabel, detailsText } = options;
    const { apiCode, hypothesis, analyticsLabel } =
      CANCELLATION_REASON_CODES[reason];

    setCancellationReason(reason);
    setRetentionOffer(null);
    setRetentionFlowError(null);
    setIsLoadingReasonSelection(true);

    // A scheduled or active pause is already the retention outcome. Do not ask
    // the server for another offer; cancellation ends the pause with the
    // subscription, and the form tells the owner that before they continue.
    // A paid early resume (RESUME_PAID) is not: the customer paid to end that
    // pause, so the server resolves the offers as for anyone else (PD12), and
    // the pause offer itself stays out through its 12-month wait.
    if (isScheduledOrActivePlanPause(subscription.planPause)) {
      trackEvent('front.settings.retention_flow_s2_continued', {
        ...generalCancellationProperties,
        final_reason_selected: hypothesis,
        reason_label: reasonLabel,
        details_text: detailsText,
        will_show_offer: false,
      });
      trackRechurn(hypothesis);
      try {
        await recordCancelDecisionUnlessRecorded();
        await scheduleCancellation({
          reason,
          outcome: 'no_offer',
          ineligibleReason: 'PLAN_PAUSE_ALREADY_EXISTS',
        });
      } finally {
        setIsLoadingReasonSelection(false);
      }
      return;
    }

    let answer: tRetentionOfferAnswer | null = null;
    let refusalCode: string | null = null;

    try {
      if (currentWorkspace?.id) {
        const response = await SubscriptionManagerService.selectRetentionReason(
          currentWorkspace.id,
          {
            phase: 'reason_selected',
            reason_code: apiCode,
            details_text: detailsText || undefined,
          },
        );
        answer =
          response?.success === true
            ? adaptRetentionOffer(response.data?.offer)
            : null;
      }
    } catch (error) {
      console.error('Error selecting retention reason:', error);
      refusalCode = readBillingRefusalCode(error);
    }

    trackEvent('front.settings.retention_flow_s2_continued', {
      ...generalCancellationProperties,
      final_reason_selected: hypothesis,
      reason_label: reasonLabel,
      details_text: detailsText,
      // Only the server knows whether an offer applies; null when its answer
      // could not be read.
      will_show_offer: answer ? answer.offer !== null : null,
    });

    // No readable answer: show the error state, from which the customer can
    // still cancel. It is never treated as "no offer".
    if (!answer) {
      setRetentionFlowError({ stage: 'lookup', code: refusalCode });
      setShowForm(false);
      setShowOfferModal(true);
      setIsLoadingReasonSelection(false);
      return;
    }

    trackRechurn(hypothesis);

    const { offer } = answer;

    if (offer) {
      setRetentionOffer(offer);
      setShowForm(false);
      setShowOfferModal(true);
      setIsLoadingReasonSelection(false);

      trackEvent('front.settings.retention_flow_s3_offer_viewed', {
        ...generalCancellationProperties,
        hypothesis,
        hypothesis_label: analyticsLabel,
        ...offerAnalyticsDetails({ offer, reason }),
      });
      return;
    }

    // The server has no offer for this reason: the cancellation is what the
    // customer asked for, so it is scheduled right away, once the session has
    // recorded that outcome. The form stays up with its loading state until the
    // manager closes the modal.
    //
    // That includes a business pause the subscription cannot take (Legacy,
    // not monthly, a Lead Agent schedule, not open to it yet…): it follows the
    // normal cancellation like any other reason, with no "pause unavailable"
    // stop on the way, and the server's reason still reaches the event.
    try {
      await recordCancelDecisionUnlessRecorded();
      await scheduleCancellation({
        reason,
        outcome: 'no_offer',
        ineligibleReason: answer.ineligibleReason,
      });
    } finally {
      setIsLoadingReasonSelection(false);
    }
  };

  /**
   * After a confirmed accept: refresh the subscription, and for a downgrade read
   * the invoice preview the success screen shows. Best effort: the offer is
   * already applied, so a failure here must not turn into an error.
   */
  const refreshAfterAcceptedOffer = async (offer: tRetentionOffer) => {
    try {
      await getSummary();

      if (offer.type !== 'DOWNGRADE' || !plans || !currentWorkspace?.id) {
        return;
      }

      const basicPlan = plans[ePlanFrequency.MONTHLY]?.find(
        (plan) => plan.planCode === ePlanCode.PLAN_BASIC,
      );
      if (!basicPlan) return;

      const basicPlanCurrency =
        basicPlan.currency === ePlanCurrency.MXN ? 'MXN' : 'USD';

      try {
        const preview = await SubscriptionManagerService.getInvoicePreview(
          currentWorkspace.id,
          {
            products: [
              {
                product_id: `${ePlanCode.PLAN_BASIC}_MONTHLY_${basicPlanCurrency}`,
                quantity: 1,
              },
            ],
          },
          new AbortController().signal,
        );
        setPlanNotSuitableInvoicePreview(preview);
      } catch (error) {
        console.error(
          'Error fetching plan not suitable invoice preview:',
          error,
        );
      }
    } catch (error) {
      console.error('Error refetching subscription summary:', error);
    }
  };

  const handleAcceptOffer = async (pauseMonths?: 1 | 2 | 3) => {
    const shownOffer = retentionOffer;
    const reason = cancellationReason;

    setRetentionFlowError(null);
    setIsLoadingOfferAcceptance(true);

    try {
      let appliedOffer: tRetentionOffer | null = null;
      let refusalCode: string | null = null;

      try {
        if (
          currentWorkspace?.id &&
          shownOffer &&
          (shownOffer.type !== 'PLAN_PAUSE' || pauseMonths !== undefined)
        ) {
          const response =
            await SubscriptionManagerService.finalizeRetentionAction(
              currentWorkspace.id,
              {
                phase: 'final_action',
                offer_accepted: true,
                final_action: 'continue',
                ...(shownOffer.type === 'PLAN_PAUSE'
                  ? { pause_months: pauseMonths }
                  : {}),
              },
            );

          if (isAppliedOfferResponse(response)) {
            // The server answers with the offer it applied, which is the one
            // shown; the shown one only covers an answer that omits it.
            appliedOffer =
              adaptRetentionOffer(response.data.offer)?.offer ?? shownOffer;
          }
        }
      } catch (error) {
        console.error('Error accepting retention offer:', error);
        refusalCode = readBillingRefusalCode(error);
      }

      if (!appliedOffer) {
        setRetentionFlowError({ stage: 'accept', code: refusalCode });
        return;
      }

      const hypothesis = hypothesisFor(reason);
      const { offer_type: offerType } = offerAnalyticsDetails({
        offer: appliedOffer,
        reason,
      });

      trackEvent('front.settings.retention_flow_s3_offer_accepted', {
        ...generalCancellationProperties,
        hypothesis,
        offer_type: offerType,
        offer_code: appliedOffer.code,
        ...(appliedOffer.type === 'PLAN_PAUSE' && pauseMonths
          ? { pause_months: pauseMonths }
          : {}),
      });

      setAppliedRetentionOffer(appliedOffer);
      setAppliedPlanPauseMonths(
        appliedOffer.type === 'PLAN_PAUSE' && pauseMonths ? pauseMonths : null,
      );
      setShowOfferModal(false);
      setShowConfirmationModal(true);

      trackEvent('front.settings.retention_flow_s4a_success_viewed', {
        ...generalCancellationProperties,
        hypothesis,
        offer_code: appliedOffer.code,
        ...(appliedOffer.type === 'PLAN_PAUSE' && pauseMonths
          ? { pause_months: pauseMonths }
          : {}),
      });

      await refreshAfterAcceptedOffer(appliedOffer);
    } finally {
      setIsLoadingOfferAcceptance(false);
    }
  };

  const handleRetryOffer = () => {
    setRetentionFlowError(null);
  };

  const handleContinueToCancellation = async () => {
    setIsLoadingFinalization(true);
    try {
      await recordCancelDecisionUnlessRecorded();
      await scheduleCancellation({
        reason: cancellationReason,
        outcome:
          retentionFlowError?.stage === 'accept'
            ? 'offer_ineligible'
            : 'offer_lookup_failed',
        ineligibleReason: null,
      });
    } finally {
      setIsLoadingFinalization(false);
    }
  };

  /**
   * Opens the booking modal when the CTA resolved to one. A CTA with no
   * configured booking page is never rendered, and an empty scheduler must
   * never open, so there is nothing to do for it.
   */
  const openRetentionSession = () => {
    if (retentionSessionCta.kind === 'booking') {
      setShowSchedulingModal(true);
    }
  };

  const handleScheduleSession = () => {
    openRetentionSession();
  };

  const handleScheduleConsultation = () => {
    openRetentionSession();
  };

  const handleCloseConfirmation = async () => {
    // If the customer opened the booking modal, refetch the subscription
    // summary when closing so the updated subscription info is shown.
    if (showSchedulingModal) {
      try {
        await getSummary();
      } catch (error) {
        console.error('Error refetching subscription summary:', error);
      }
    }
    resetLocalCancellationStates();
    resetCancellationModalStates();
  };

  /**
   * The customer declined the offer shown. The decline is recorded first and
   * the cancellation is scheduled after it, exactly once. A decline that could
   * not be recorded does not keep the customer from cancelling; it is only not
   * reported as recorded.
   */
  const handleFinalizeSubscription = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    const reason = cancellationReason;
    const shownOffer = retentionOffer;

    setIsLoadingFinalization(true);

    try {
      const declineRecorded = await recordCancelDecision();

      if (declineRecorded) {
        trackEvent('front.settings.retention_flow_s3_offer_rejected', {
          ...generalCancellationProperties,
          hypothesis: hypothesisFor(reason),
          ...(shownOffer
            ? {
                offer_type: offerAnalyticsType({ offer: shownOffer, reason }),
                offer_code: shownOffer.code,
              }
            : {}),
        });
      }

      await scheduleCancellation({
        reason,
        outcome: 'offer_rejected',
        ineligibleReason: null,
      });
    } finally {
      setIsLoadingFinalization(false);
    }
  };

  const calculateDates = () => {
    const today = moment();
    // Use the end of the current subscription period as the next charge date
    const nextChargeDate = subscription.endDate
      ? moment(subscription.endDate)
      : moment(today).add(1, 'months');

    const formatDate = (date: moment.Moment): string => {
      // Reuse the existing dateToLocaleDateString function to avoid code duplication
      return dateToLocaleDateString(date.toDate()) || '';
    };

    return {
      nextChargeDate,
      formatDate,
    };
  };

  return {
    // State
    showForm,
    showOfferModal,
    showConfirmationModal,
    showSchedulingModal,
    retentionSessionCta,
    cancellationReason,
    retentionOffer,
    appliedRetentionOffer,
    appliedPlanPauseMonths,
    retentionFlowError,
    leadsByFunnel,
    isLoadingLeads,
    sentMessagesCount,
    hasZeroLeads,
    activeFunnelsCount,
    totalLeadsCount,
    userNames,
    remainingUsersCount,
    isLoadingReasonSelection,
    isLoadingOfferAcceptance,
    isLoadingFinalization,
    planNotSuitableInvoicePreview,

    // Handlers
    setShowForm,
    handleKeepSubscription,
    handleContinue,
    handleBackToReasons,
    handleClose,
    handleSelectReason,
    handleAcceptOffer,
    handleRetryOffer,
    handleContinueToCancellation,
    handleCloseConfirmation,
    handleScheduleSession,
    handleScheduleConsultation,
    handleFinalizeSubscription,

    calculateDates,
  };
}
